import { Typography } from "antd"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts"

import Icon from "../../Icon"
import { GLOBAL_MAP_SURFACE_CLASS } from "./global-map-surface.ts"
import { globalMapStatusStyle } from "./helpers/status-style"
import {
  summarizeByLocation,
  summarizeByStatus,
  type SummaryRow
} from "./helpers/map-summary.ts"
import type { MapFeature } from "./redux/global-map.types"

const { Title, Text } = Typography

/**
 * The two map-context cards that sit beside Recent Activity under the map.
 *
 * Both are derived in the browser from the SAME `/features` payload the map is
 * already rendering (`helpers/map-summary.ts`) — no second request, no new
 * endpoint, no backend change. That is also what makes them map context and
 * not a second Analytics tab: they describe exactly the records currently
 * plotted, and they move with the filters because the response does.
 *
 * A clustered response lists no individual records, so neither card can be
 * computed; each says so rather than showing a count that would silently
 * exclude everything the server rolled into clusters.
 */

function SummaryCard({
  title,
  isClustered,
  rows,
  children
}: {
  title: string
  isClustered: boolean
  rows: SummaryRow[]
  children: React.ReactNode
}) {
  const { t } = useTranslation()
  return (
    <div className={`flex min-w-0 flex-col gap-3 p-4 ${GLOBAL_MAP_SURFACE_CLASS}`}>
      <Title level={5} className="!mb-0 !text-[15px] font-semibold !text-[#0F172A]">
        {title}
      </Title>
      {isClustered ? (
        <div className="flex flex-1 items-center justify-center px-2 text-center">
          <Text className="text-xs text-slate-400">
            {t("globalMap.mapSummary.clustered")}
          </Text>
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-1 items-center justify-center px-2 text-center">
          <Text className="text-xs text-slate-400">
            {t("globalMap.mapSummary.empty")}
          </Text>
        </div>
      ) : (
        children
      )}
    </div>
  )
}

interface MapContextSummariesProps {
  features: MapFeature[]
  // `true` when the backend answered in cluster mode, where no individual
  // record is enumerated and neither summary can honestly be computed.
  isClustered: boolean
}

export function ProjectsByStatusCard({
  features,
  isClustered
}: MapContextSummariesProps) {
  const { t } = useTranslation()
  const rows = useMemo(() => summarizeByStatus(features), [features])
  const total = rows.reduce((sum, row) => sum + row.count, 0)

  // The canonical per-status colour every other Global Map surface uses, so a
  // slice matches the pill on the row it stands for. Keyed off the entity the
  // label actually came from.
  const colored = rows.map((row) => {
    const owner = features.find((f) => f.status_label === row.label)
    return {
      ...row,
      color: owner
        ? globalMapStatusStyle(owner.entity_type, row.label).color
        : "#94A3B8"
    }
  })

  return (
    <SummaryCard
      title={t("globalMap.mapSummary.byStatus")}
      isClustered={isClustered}
      rows={rows}
    >
      {/* The legend needs ~100px of its own before its status names start
          truncating to nothing, so it wraps under the donut rather than
          being squeezed beside it once this card is narrow — which it is at
          1440 and below, where the three cards split the map column. */}
      <div className="flex min-h-0 flex-1 flex-wrap items-center justify-center gap-4">
        {/* The donut grows with the row: the three cards share one grid row,
            so when Recent Activity is long this card gets taller and the
            chart uses it rather than leaving a dead band under a fixed
            136px circle. Percentage radii let recharts scale the ring with
            its own measured box, and the square box is bounded at both ends
            so it can never go tiny on a 1366 laptop or grotesque on a tall
            row. */}
        <div className="relative aspect-square h-full max-h-[190px] min-h-[120px] w-auto min-w-[120px] max-w-[190px] shrink-0 self-center">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={colored}
                dataKey="count"
                nameKey="label"
                innerRadius="64%"
                outerRadius="98%"
                paddingAngle={1}
                isAnimationActive={false}
              >
                {colored.map((row) => (
                  <Cell key={row.key} fill={row.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[26px] font-bold leading-none text-slate-900">
              {total}
            </span>
            <span className="mt-1 text-[11px] text-slate-500">
              {t("globalMap.mapSummary.records")}
            </span>
          </div>
        </div>
        <ul className="m-0 flex min-w-[100px] flex-1 list-none flex-col gap-2 p-0">
          {colored.slice(0, 6).map((row) => (
            <li
              key={row.key}
              className="flex items-center gap-2.5 text-[13px] leading-none"
            >
              <span
                aria-hidden
                className="size-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: row.color }}
              />
              <span className="min-w-0 flex-1 truncate text-slate-700">
                {row.label}
              </span>
              <span className="shrink-0 font-semibold tabular-nums text-slate-900">
                {row.count}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </SummaryCard>
  )
}

export function TopLocationsCard({
  features,
  isClustered
}: MapContextSummariesProps) {
  const { t } = useTranslation()
  const rows = useMemo(() => summarizeByLocation(features), [features])
  const max = Math.max(...rows.map((row) => row.count), 1)

  return (
    <SummaryCard
      title={t("globalMap.mapSummary.topLocations")}
      isClustered={isClustered}
      rows={rows}
    >
      {/* `flex-1` + `justify-around` spreads the rows down the card instead of
          stacking them against the heading, so this card reads as deliberate
          beside the donut rather than top-heavy. */}
      <ul className="m-0 flex flex-1 list-none flex-col justify-around gap-1 p-0">
        {rows.map((row, index) => (
          <li key={row.key} className="flex items-center gap-2.5">
            <Icon type="Map" className="shrink-0 text-[15px] text-blue-600" />
            <span className="w-24 shrink-0 truncate text-[13px] font-medium text-slate-800">
              {row.label}
            </span>
            <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
              {/* The busiest location carries the full-strength blue; the rest
                  step back, so the ranking reads before the numbers do. */}
              <span
                className={`block h-full rounded-full ${index === 0 ? "bg-blue-600" : "bg-blue-400"}`}
                style={{ width: `${(row.count / max) * 100}%` }}
              />
            </span>
            <span className="w-6 shrink-0 text-right text-[13px] font-semibold tabular-nums text-slate-900">
              {row.count}
            </span>
          </li>
        ))}
      </ul>
    </SummaryCard>
  )
}
